import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { colors } from '../utils/theme';
import { Edit3, Smartphone, X, ChevronLeft, ChevronRight } from 'lucide-react';

const Devices = () => {
  const [devices, setDevices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  
  // Form & Pagination State
  const [selectedEmp, setSelectedEmp] = useState("");
  const [status, setStatus] = useState("AVAILABLE");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    fetchData();
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchData = async () => {
    try {
      const devRes = await axios.get("https://gpsapi.emedha.in/web/api/devices");
      const empRes = await axios.get("https://gpsapi.emedha.in/web/api/employee");
      setDevices(devRes.data);
      setFilteredDevices(devRes.data);
      setEmployees(empRes.data);
    } catch (e) { console.error("Fetch error", e); }
  };

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredDevices.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredDevices.length / itemsPerPage);

  const handleEdit = (device) => {
    setEditingDevice(device);
    setSelectedEmp(device.employee_id || "");
    setStatus(device.status);
    setShowModal(true);
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`https://gpsapi.emedha.in/web/api/devices/${editingDevice.id}`, {
        employee_id: selectedEmp,
        status: status,
        device_id: editingDevice.device_id
      });
      Swal.fire({ title: 'Success', text: 'Device updated', icon: 'success', confirmButtonColor: colors.primary });
      setShowModal(false);
      fetchData();
    } catch (error) { Swal.fire('Error', 'Update failed', 'error'); }
  };

  const isMobile = windowWidth < 768;

  return (
    <div style={styles.pageWrapper}>
      <div style={styles.mainContainer}>
        
        <header style={styles.header}>
          <div style={{ flex: 1 }}>
            <h1 style={{ ...styles.title, fontSize: isMobile ? '22px' : '32px' }}>Inventory Management</h1>
            <p style={{ ...styles.subtitle, fontSize: isMobile ? '12px' : '14px' }}>
              Real-time monitoring of corporate assets and assignments
            </p>
          </div>
          <div style={styles.statsCount}>
            <Smartphone size={isMobile ? 18 : 22} color={colors.primary} />
            <span style={styles.statsText}>{devices.length} Devices</span>
          </div>
        </header>

        <div style={styles.card}>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Device Info</th>
                  <th style={styles.th}>Device ID</th>
                  <th style={styles.th}>Assigned To</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((dev) => (
                  <tr key={dev.id} style={styles.tr}>
                    <td style={styles.td}>
                      <div style={styles.centeredCol}>
                        <div style={styles.brandText}>{dev.brand}</div>
                        <div style={styles.modelText}>{dev.model}</div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <code style={styles.code}>{isMobile ? '...' + dev.device_id.slice(-6) : dev.device_id}</code>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.centeredCol}>
                        {dev.employee_name ? (
                          <div style={styles.assignedContainer}>
                            <div style={styles.avatar}>{dev.employee_name[0]}</div>
                            <span style={styles.empName}>{dev.employee_name}</span>
                          </div>
                        ) : <span style={{ color: '#ccc', fontSize: '13px' }}>Available</span>}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{ 
                        ...styles.statusBadge, 
                        backgroundColor: dev.status === 'AVAILABLE' ? '#e6fffa' : dev.status === 'ASSIGNED' ? '#fff5f5' : '#f7fafc',
                        color: dev.status === 'AVAILABLE' ? '#319795' : dev.status === 'ASSIGNED' ? colors.primary : '#4a5568'
                      }}>
                        {dev.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <button onClick={() => handleEdit(dev)} style={styles.editBtn}>
                        <Edit3 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination Footer */}
            <div style={styles.paginationFooter}>
              <div style={styles.paginationInfo}>
                Showing <b>{indexOfFirstItem + 1}</b> to <b>{Math.min(indexOfLastItem, filteredDevices.length)}</b> of <b>{filteredDevices.length}</b> devices
              </div>
              
              <div style={styles.paginationControls}>
                <select 
                  value={itemsPerPage} 
                  onChange={(e) => {setItemsPerPage(Number(e.target.value)); setCurrentPage(1);}}
                  style={styles.pageSelect}
                >
                  {[5, 10, 20].map(val => <option key={val} value={val}>{val} per page</option>)}
                </select>

                <div style={styles.pageNav}>
                  <button 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => prev - 1)}
                    style={{ ...styles.navBtn, opacity: currentPage === 1 ? 0.3 : 1 }}
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span style={styles.pageNum}>Page {currentPage} of {totalPages || 1}</span>
                  <button 
                    disabled={currentPage === totalPages || totalPages === 0}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    style={{ ...styles.navBtn, opacity: (currentPage === totalPages || totalPages === 0) ? 0.3 : 1 }}
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Assignment Modal - Same as before but styled consistently */}
      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, width: isMobile ? '90%' : '450px' }}>
            <div style={styles.modalHeader}>
              <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '800' }}>Update Assignment</h2>
              <X cursor="pointer" size={20} onClick={() => setShowModal(false)} />
            </div>
            <div style={styles.modalBody}>
              <label style={styles.label}>Select Staff Member</label>
              <select 
                style={styles.select} 
                value={selectedEmp} 
                onChange={(e) => {
                  setSelectedEmp(e.target.value);
                  if(e.target.value) setStatus("ASSIGNED");
                }}
              >
                <option value="">No Assignment (Available)</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>

              <label style={styles.label}>Inventory Status</label>
              <select style={styles.select} value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="AVAILABLE">AVAILABLE</option>
                <option value="ASSIGNED">ASSIGNED</option>
                <option value="BLOCKED">BLOCKED</option>
              </select>

              <button onClick={handleUpdate} style={styles.saveBtn}>Update Asset Record</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  pageWrapper: { backgroundColor: colors.background, minHeight: '100vh', display: 'flex', justifyContent: 'center', width: '100%' },
  mainContainer: { width: '100%', maxWidth: '1200px', padding: '20px', display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px', marginTop: '20px' },
  title: { fontWeight: '900', color: colors.text, margin: 0, letterSpacing: '-0.5px' },
  subtitle: { color: '#888', marginTop: 5, fontWeight: '500' },
  statsCount: { backgroundColor: '#fff', padding: '10px 20px', borderRadius: '15px', display: 'flex', alignItems: 'center', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' },
  statsText: { fontWeight: '800', marginLeft: 10, color: '#333', fontSize: '14px' },
  card: { backgroundColor: '#fff', borderRadius: '25px', boxShadow: '0 15px 40px rgba(0,0,0,0.04)', border: '1px solid #f1f1f1', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'center', padding: '18px 12px', color: '#bbb', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid #f7f7f7' },
  td: { padding: '18px 12px', borderBottom: '1px solid #fbfbfb', textAlign: 'center' },
  centeredCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  brandText: { fontWeight: '800', fontSize: '15px', color: '#1a1a1a' },
  modelText: { fontSize: '11px', color: '#aaa', textTransform: 'uppercase', marginTop: 2 },
  code: { backgroundColor: '#f8f9fa', padding: '5px 10px', borderRadius: '8px', fontSize: '12px', color: colors.primary, fontWeight: '600', display: 'inline-block' },
  statusBadge: { padding: '6px 14px', borderRadius: '10px', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', display: 'inline-block' },
  editBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primary + '10', color: colors.primary, border: 'none', width: 40, height: 40, borderRadius: '12px', cursor: 'pointer' },
  avatar: { width: 30, height: 30, borderRadius: 10, backgroundColor: colors.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold' },
  assignedContainer: { display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' },
  empName: { fontWeight: '600', fontSize: '14px', color: '#444' },
  
  // Pagination Styles
  paginationFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 25px', backgroundColor: '#fff', borderTop: '1px solid #f7f7f7', flexWrap: 'wrap', gap: '15px' },
  paginationInfo: { fontSize: '13px', color: '#888' },
  paginationControls: { display: 'flex', alignItems: 'center', gap: '20px' },
  pageSelect: { padding: '8px 12px', borderRadius: '10px', border: '1px solid #eee', fontSize: '12px', fontWeight: '600', outline: 'none', cursor: 'pointer' },
  pageNav: { display: 'flex', alignItems: 'center', gap: '10px' },
  navBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '8px', border: '1px solid #eee', backgroundColor: '#fff', cursor: 'pointer' },
  pageNum: { fontSize: '13px', fontWeight: '700', color: '#333' },

  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(25, 25, 25, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' },
  modal: { backgroundColor: '#fff', borderRadius: '30px', padding: '35px', boxShadow: '0 30px 60px rgba(0,0,0,0.2)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
  label: { display: 'block', fontSize: '11px', fontWeight: '800', marginBottom: 10, color: '#999', textTransform: 'uppercase' },
  select: { width: '100%', padding: '15px', borderRadius: '15px', border: '1.5px solid #f0f0f0', marginBottom: '25px', outline: 'none', backgroundColor: '#fafafa', fontSize: '14px', fontWeight: '600' },
  saveBtn: { width: '100%', padding: '18px', backgroundColor: colors.primary, color: '#fff', border: 'none', borderRadius: '18px', fontWeight: '800', cursor: 'pointer', boxShadow: `0 10px 25px ${colors.primary}40`, fontSize: '15px' }
};

export default Devices;